// src/components/PlacementCard.jsx

import { motion } from "framer-motion";
import {
  HiAcademicCap,
  HiArrowRight,
  HiBriefcase,
  HiCheckBadge,
} from "react-icons/hi2";

const PlacementCard = ({
  student,
  featured = false,
}) => {
  const studentName =
    student?.name || "Student Name";

  const companyName =
    student?.company || "Company Name";

  const courseName =
    student?.course || "Professional Program";

  const packageValue =
    student?.package || "—";

  const location =
    student?.location || null;

  const companyInitials = companyName
    .slice(0, 2)
    .toUpperCase();

  const studentInitial =
    studentName.charAt(0).toUpperCase();

  return (
    <motion.article
      id={`placement-${student?.id}`}
      whileHover={{
        y: featured ? -4 : -3,
      }}
      transition={{
        duration: 0.25,
        ease: "easeOut",
      }}
      className={`
        group
        relative
        flex
        h-full
        flex-col
        overflow-hidden
        rounded-[22px]
        border
        border-slate-200
        bg-white
        shadow-[0_8px_30px_rgba(15,23,42,0.045)]
        transition-all
        duration-300
        hover:border-indigo-200
        hover:shadow-[0_16px_42px_rgba(79,70,229,0.09)]
        dark:border-slate-800
        dark:bg-slate-900
        dark:hover:border-indigo-800
        ${
          featured
            ? "lg:rounded-[24px]"
            : ""
        }
      `}
    >
      {/* =====================================================
          TOP ACCENT
      ====================================================== */}

      <div
        aria-hidden="true"
        className="
          absolute
          inset-x-0
          top-0
          z-20
          h-[2px]
          bg-gradient-to-r
          from-indigo-500
          via-blue-500
          to-cyan-400
        "
      />

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div
        className="
          flex
          items-center
          px-5
          pb-0
          pt-5
          sm:px-6
          sm:pt-6
        "
      >
        {featured && (
          <span
            className="
              inline-flex
              items-center
              gap-1.5
              rounded-full
              bg-indigo-50
              px-2.5
              py-1
              text-[8px]
              font-bold
              uppercase
              tracking-[0.13em]
              text-indigo-600
              dark:bg-indigo-950/50
              dark:text-indigo-300
            "
          >
            <HiCheckBadge className="text-xs" />
            Featured
          </span>
        )}
      </div>

      {/* =====================================================
          PROFILE
      ====================================================== */}

      <div
        className="
          flex
          gap-4
          px-5
          pb-5
          pt-5
          sm:gap-5
          sm:px-6
          sm:pb-6
        "
      >
        {/* Student image */}

        <div
          className={`
            relative
            shrink-0
            overflow-hidden
            rounded-[18px]
            bg-gray-100
            dark:bg-gray-950
            ${
              featured
                ? "h-[145px] w-[125px]"
                : "h-[125px] w-[105px]"
            }
          `}
        >
          {student?.image ? (
            <>
              {/* Soft image background */}

              <img
                src={student.image}
                alt=""
                aria-hidden="true"
                className="
                  absolute
                  inset-0
                  h-full
                  w-full
                  scale-110
                  object-cover
                  opacity-15
                  blur-xl
                "
              />

              <div
                className="
                  absolute
                  inset-0
                  bg-white/70
                  dark:bg-gray-950/70
                "
              />

              {/* Complete image */}

              <div
                className="
                  absolute
                  inset-2
                  flex
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[14px]
                  border
                  border-white/60
                  dark:border-white/10
                "
              >
                <img
                  src={student.image}
                  alt={studentName}
                  loading="lazy"
                  className="
                    max-h-full
                    max-w-full
                    object-contain
                    transition-transform
                    duration-500
                    group-hover:scale-[1.025]
                  "
                />
              </div>
            </>
          ) : (
            <div
              className="
                flex
                h-full
                w-full
                items-center
                justify-center
                bg-indigo-50
                dark:bg-indigo-950
              "
            >
              <span
                className="
                  text-4xl
                  font-extrabold
                  text-indigo-300
                  dark:text-indigo-700
                "
              >
                {studentInitial}
              </span>
            </div>
          )}
        </div>

        {/* Student information */}

        <div className="min-w-0 flex-1 py-1">

          {/* Course */}

          <p
            className="
              text-[8px]
              font-bold
              uppercase
              tracking-[0.15em]
              text-indigo-600
              dark:text-indigo-400
            "
          >
            {courseName}
          </p>

          {/* Name */}

          <h3
            className={`
              mt-2
              font-extrabold
              leading-[1.05]
              tracking-[-0.035em]
              text-gray-950
              dark:text-white
              ${
                featured
                  ? "text-xl sm:text-2xl"
                  : "text-lg sm:text-xl"
              }
            `}
          >
            {studentName}
          </h3>

          {/* Role only when actually provided */}

          {student?.role && (
            <p
              className="
                mt-2
                text-[10px]
                font-medium
                leading-5
                text-gray-500
                dark:text-gray-400
              "
            >
              {student.role}
            </p>
          )}

          {/* Company */}

          <div
            className="
              mt-4
              flex
              min-w-0
              items-center
              gap-2.5
            "
          >
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-lg
                border
                border-gray-200
                bg-gray-50
                p-1.5
                dark:border-gray-700
                dark:bg-gray-800
              "
            >
              {student?.companyLogo ? (
                <img
                  src={student.companyLogo}
                  alt={companyName}
                  className="
                    h-full
                    w-full
                    object-contain
                  "
                />
              ) : (
                <span
                  className="
                    text-[8px]
                    font-extrabold
                    text-indigo-600
                    dark:text-indigo-300
                  "
                >
                  {companyInitials}
                </span>
              )}
            </div>

            <div className="min-w-0">
              <p
                className="
                  text-[7px]
                  font-bold
                  uppercase
                  tracking-[0.12em]
                  text-gray-400
                "
              >
                Placed at
              </p>

              <p
                className="
                  truncate
                  text-[11px]
                  font-extrabold
                  text-gray-900
                  dark:text-white
                "
              >
                {companyName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          PACKAGE STRIP
      ====================================================== */}

      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        <div
          className="
            relative
            overflow-hidden
            rounded-[17px]
            bg-gray-950
            px-4
            py-3.5
            dark:bg-black
          "
        >
          {/* Glow */}

          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute
              right-[-30px]
              top-[-50px]
              h-[130px]
              w-[130px]
              rounded-full
              bg-indigo-500/25
              blur-[45px]
            "
          />

          <div
            className="
              relative
              flex
              items-center
              justify-between
              gap-4
            "
          >
            <div>
              <p
                className="
                  text-[7px]
                  font-bold
                  uppercase
                  tracking-[0.14em]
                  text-white/45
                "
              >
                Annual Package
              </p>

              <p
                className="
                  mt-0.5
                  text-xl
                  font-extrabold
                  tracking-[-0.04em]
                  text-white
                  sm:text-2xl
                "
              >
                {packageValue}
              </p>
            </div>

            <div
              className="
                flex
                h-9
                w-9
                items-center
                justify-center
                rounded-xl
                bg-white/10
                text-cyan-300
              "
            >
              <HiBriefcase className="text-base" />
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          FOOTER
      ====================================================== */}

      <div
        className="
          flex
          items-center
          justify-between
          gap-3
          border-t
          border-gray-100
          px-5
          py-3.5
          dark:border-gray-800
          sm:px-6
        "
      >
        <div
          className="
            flex
            min-w-0
            items-center
            gap-2
          "
        >
          <HiAcademicCap
            className="
              shrink-0
              text-sm
              text-indigo-500
            "
          />

          <span
            className="
              truncate
              text-[8px]
              font-medium
              text-gray-500
              dark:text-gray-400
            "
          >
            {courseName}
          </span>

          {/* Location is shown only when supplied */}

          {location && (
            <>
              <span className="text-gray-300 dark:text-gray-700">
                •
              </span>

              <span
                className="
                  truncate
                  text-[8px]
                  font-medium
                  text-gray-500
                  dark:text-gray-400
                "
              >
                {location}
              </span>
            </>
          )}
        </div>

        <span
          className="
            inline-flex
            shrink-0
            items-center
            gap-1
            text-[8px]
            font-bold
            uppercase
            tracking-[0.08em]
            text-indigo-600
            dark:text-indigo-400
          "
        >
          Placement

          <HiArrowRight
            className="
              text-xs
              transition-transform
              duration-300
              group-hover:translate-x-1
            "
          />
        </span>
      </div>
    </motion.article>
  );
};

export default PlacementCard;